import { z } from "zod";
import { BusinessDate, EntityBase, Name, Note } from "./common.js";
import { Id } from "./ids.js";

/**
 * Menu contracts (architecture §7.2 "catalogue", requirements §5). Every list here is manager-editable data,
 * never a fixed list in code (DL-003): meals, tiers, items and companion packages.
 */

/** Short code shown on slips and used for Excel mapping; the code pattern is a setting (req §5.2). */
export const ItemCode = z.string().trim().min(1).max(24);

/** A meal service: lunch, dinner, and whatever the manager adds (breakfast, iftar, sahari …). */
export const Meal = EntityBase.extend({
  name: Name,
  sortOrder: z.int().nonnegative(),
  active: z.boolean(),
});
export type Meal = z.infer<typeof Meal>;

/**
 * Portion tier: one axis (DL-030). Seeded as normal / special / economy; the manager may add more.
 * Not to be confused with the hidden kitchen upgrade (`KitchenUpgrade`, DL-019).
 */
export const Tier = EntityBase.extend({
  name: Name,
  sortOrder: z.int().nonnegative(),
  active: z.boolean(),
});
export type Tier = z.infer<typeof Tier>;

/**
 * A menu item. `food` is ordered per tier; `companion` is a side item (drink, yoghurt, olives, bread, cutlery …)
 * that comes in a tier's package or is ordered as an extra line (req §5.5).
 */
export const MenuItemKind = z.enum(["food", "companion"]);
export type MenuItemKind = z.infer<typeof MenuItemKind>;

/**
 * Which kitchen station prepares a companion (architecture §8.3): bread goes to the bread station, everything else
 * (drinks, yoghurt, olives, cutlery …) to the companion station. Food items have none. Bread amounts per person
 * come from the companion packages (DL-057, AS-038); there is no second bread ratio to keep in step.
 */
export const PrepStation = z.enum(["bread", "companions"]);
export type PrepStation = z.infer<typeof PrepStation>;

export const MenuItem = EntityBase.extend({
  code: ItemCode,
  name: Name,
  kind: MenuItemKind,
  description: Note,
  active: z.boolean(),
  prepStation: PrepStation.nullable(),
});
export type MenuItem = z.infer<typeof MenuItem>;
export const MenuItemInput = MenuItem.pick({
  code: true,
  name: true,
  kind: true,
  description: true,
  active: true,
}).extend({ prepStation: PrepStation.nullable().default(null) });
export type MenuItemInput = z.infer<typeof MenuItemInput>;

/**
 * Companion package per food × tier (DL-030, DL-032): e.g. normal = soft drink; special = soft drink + olives.
 * `included` companions are part of the portion price; the others are charged on their own price rule.
 * An organization may override the package (req §5.5) — `organizationId` null is the default package.
 */
export const CompanionPackageLine = z.object({
  companionItemId: Id,
  /** Units per portion, in thousandths so 1.5 bread per person is 1500 (no floats, AGENTS.md §3.6). */
  perPortionMilli: z.int().positive().max(100_000),
  included: z.boolean(),
});
export type CompanionPackageLine = z.infer<typeof CompanionPackageLine>;

export const CompanionPackage = EntityBase.extend({
  foodItemId: Id,
  tierId: Id,
  organizationId: Id.nullable(),
  lines: z.array(CompanionPackageLine).max(30),
});
export type CompanionPackage = z.infer<typeof CompanionPackage>;

/**
 * Who a menu entry is for. `organization` entries form that organization's own menu (DL-031) and apply to all
 * its descendants; the nearest organization with entries for a (date, meal) wins over the global menu.
 */
export const MenuAudience = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("global") }),
  z.object({ kind: z.literal("organization"), id: Id }),
]);
export type MenuAudience = z.infer<typeof MenuAudience>;

/** One food on one day's menu for one meal (Shamsi calendar in the UI, ISO date in storage). */
export const MenuDayEntry = EntityBase.extend({
  businessDate: BusinessDate,
  mealId: Id,
  menuItemId: Id,
  audience: MenuAudience,
  /** Tiers offered for this food on this day; empty = every active tier. */
  tierIds: z.array(Id).max(10),
  sortOrder: z.int().nonnegative(),
});
export type MenuDayEntry = z.infer<typeof MenuDayEntry>;

/** "Copy last week" and weekly/monthly patterns (req §5.3): copies entries, never overwrites without asking. */
export const MenuCopyRequest = z
  .object({
    fromDate: BusinessDate,
    toDate: BusinessDate,
    days: z.int().min(1).max(31),
    audience: MenuAudience,
    /** When the target already has entries: keep them (skip that day) or replace them. */
    onExisting: z.enum(["skip", "replace"]),
  })
  .refine((r) => r.fromDate !== r.toDate, { message: "source and target must differ" });
export type MenuCopyRequest = z.infer<typeof MenuCopyRequest>;

/** The effective menu a unit sees for one (date, meal), after resolving the organization tree. */
export const EffectiveMenu = z.object({
  businessDate: BusinessDate,
  mealId: Id,
  /** Where the entries came from: an organization's own menu or the global one. */
  source: MenuAudience,
  items: z.array(
    z.object({
      menuItemId: Id,
      code: ItemCode,
      name: Name,
      tierIds: z.array(Id),
    }),
  ),
});
export type EffectiveMenu = z.infer<typeof EffectiveMenu>;

// ---------------------------------------------------------------------------------------------------------------
// Components and recipes (architecture §8.5 الف, ADR-22). A food is made of components; components are shared, so
// the rice of two foods is cooked in one pot (AS-035). A food without a recipe is planned as one component.

export const Component = EntityBase.extend({
  name: Name,
  sortOrder: z.int().nonnegative(),
  active: z.boolean(),
});
export type Component = z.infer<typeof Component>;
export const ComponentInput = Component.pick({ name: true, sortOrder: true, active: true });
export type ComponentInput = z.infer<typeof ComponentInput>;

export const RecipeLine = z.object({
  componentId: Id,
  /** Component portions per food portion, in thousandths (1000 = one portion of rice per portion of food). */
  perPortionMilli: z.int().positive().max(10_000),
});
export type RecipeLine = z.infer<typeof RecipeLine>;

export const Recipe = z.object({
  menuItemId: Id,
  /** null = no recipe yet: the food is planned as one component of its own name. Required to replace. */
  version: z.int().nonnegative().nullable(),
  lines: z
    .array(RecipeLine)
    .max(12)
    .refine((ls) => new Set(ls.map((l) => l.componentId)).size === ls.length, {
      message: "a component appears once per recipe",
    }),
});
export type Recipe = z.infer<typeof Recipe>;
export const RecipeInput = Recipe.pick({ version: true, lines: true });
export type RecipeInput = z.infer<typeof RecipeInput>;
